/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";

export type VarianceReport = {
  totals: { sold_value: number; wasted_value: number; unexplained_value: number };
  items: {
    stock_item_id: number;
    name: string;
    base_unit: string;
    received: number;
    sold: number;
    wasted: number;
    unexplained: number;
    unexplained_value: number;
    variance_pct: number | null;
  }[];
};

/** Unexplained usage beyond this share of what recipes predict is flagged. */
const VARIANCE_ALERT_PCT = 5;

type Props = { report: VarianceReport };

// Per ingredient: what recipes say was used (sold), what was logged as
// waste, and what counts found unexplained — the actual vs theoretical gap.
export const VarianceTable = defineComponent<Props>({
  name: "VarianceTable",
  props: ["report"],
  setup(props) {
    const { t } = useLocalization();
    const qty = (v: number, unit: string) => `${Number(v.toFixed(2))} ${unit}`;

    return () => (
      <div class="space-y-2">
        <div>
          <h3 class="text-base font-semibold">{t("inventory.variance.title")}</h3>
          <p class="text-xs text-base-content/50">{t("inventory.variance.subtitle")}</p>
        </div>
        <div class="flex flex-wrap gap-2 text-sm">
          <span class="badge badge-ghost">
            {t("inventory.variance.sold")}: {props.report.totals.sold_value.toFixed(2)}
          </span>
          <span class="badge badge-ghost">
            {t("inventory.variance.wasted")}: {props.report.totals.wasted_value.toFixed(2)}
          </span>
          <span class={["badge", props.report.totals.unexplained_value < 0 ? "badge-error" : "badge-ghost"].join(" ")}>
            {t("inventory.variance.unexplained")}: {props.report.totals.unexplained_value.toFixed(2)}
          </span>
        </div>
        {props.report.items.length === 0 ? (
          <p class="text-sm text-base-content/60">{t("inventory.variance.empty")}</p>
        ) : (
          <div class="overflow-x-auto rounded-lg border border-base-300 bg-base-100">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>{t("inventory.recipe.col_item")}</th>
                  <th class="text-right">{t("inventory.variance.received")}</th>
                  <th class="text-right">{t("inventory.variance.sold")}</th>
                  <th class="text-right">{t("inventory.variance.wasted")}</th>
                  <th class="text-right">{t("inventory.variance.unexplained")}</th>
                  <th class="text-right">{t("inventory.counts.value")}</th>
                  <th class="text-right">{t("inventory.variance.pct")}</th>
                </tr>
              </thead>
              <tbody>
                {props.report.items.map((i) => {
                  const alert = i.variance_pct != null && Math.abs(i.variance_pct) > VARIANCE_ALERT_PCT;
                  return (
                    <tr key={i.stock_item_id}>
                      <td>{i.name}</td>
                      <td class="text-right">{qty(i.received, i.base_unit)}</td>
                      <td class="text-right">{qty(i.sold, i.base_unit)}</td>
                      <td class="text-right">{qty(i.wasted, i.base_unit)}</td>
                      <td class={["text-right", i.unexplained < 0 ? "text-error" : ""].join(" ")}>
                        {qty(i.unexplained, i.base_unit)}
                      </td>
                      <td class="text-right">{i.unexplained_value.toFixed(2)}</td>
                      <td class={["text-right font-medium", alert ? "text-error" : ""].join(" ")}>
                        {i.variance_pct == null ? "—" : `${i.variance_pct.toFixed(1)}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  },
});
